"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SocketIoManager = void 0;
const io = require("socket.io-client");
const uuid = require("uuid");
const atlas_engine_sdk_1 = require("@atlas-engine/atlas_engine_sdk");
const index_1 = require("../../Types/index");
class SocketIoManager {
    constructor(atlasEngineUrl) {
        this.socketCollection = {};
        this.subscriptionCollection = {};
        this.atlasEngineUrl = atlasEngineUrl;
    }
    /**
     * Disconnects the socket associated with the given identity.
     *
     * @param identity The identity to disconnect.
     */
    disconnectSocket(identity) {
        const socketForIdentity = this.getSocketForIdentity(identity);
        if (!socketForIdentity) {
            return;
        }
        socketForIdentity.disconnect();
        socketForIdentity.close();
        delete this.socketCollection[identity.userId];
    }
    /**
     * Disposes all sockets.
     */
    dispose() {
        const sockets = Object.keys(this.socketCollection);
        for (const socketId of sockets) {
            this.socketCollection[socketId].disconnect();
            this.socketCollection[socketId].close();
            delete this.socketCollection[socketId];
        }
    }
    /**
     * Creates a new socket.io subscription for the given eventName.
     *
     * @param identity      The identity for which to create the subscription.
     * @param eventName     The event to subscribe to.
     * @param callback      The callback to execute when the event gets triggered.
     * @param subscribeOnce If true, the subscription will be disposed after the event was received once.
     */
    createSocketIoSubscription(identity, eventName, callback, subscribeOnce) {
        const socketForIdentity = this.createSocketForIdentity(identity);
        if (subscribeOnce) {
            socketForIdentity.once(eventName, callback);
        }
        else {
            socketForIdentity.on(eventName, callback);
        }
        const subscriptionId = uuid.v4();
        const subscription = {
            id: subscriptionId,
            eventName: eventName,
            onlyReceiveOnce: subscribeOnce,
        };
        this.subscriptionCollection[subscriptionId] = callback;
        return subscription;
    }
    /**
     * Removes the given subscription from the socket associated with the given identity.
     *
     * @param identity     The identity for which to remove a subscription.
     * @param subscription The subscription to remove.
     */
    removeSocketIoSubscription(identity, subscription) {
        const socketForIdentity = this.getSocketForIdentity(identity);
        if (!socketForIdentity) {
            return;
        }
        const callbackToRemove = this.subscriptionCollection[subscription.id];
        if (!callbackToRemove) {
            return;
        }
        socketForIdentity.off(subscription.eventName, callbackToRemove);
        delete this.subscriptionCollection[subscription.id];
    }
    getSocketForIdentity(identity) {
        return this.socketCollection[identity.userId];
    }
    createSocketForIdentity(identity) {
        const existingSocket = this.getSocketForIdentity(identity);
        if (existingSocket) {
            return existingSocket;
        }
        const noAuthTokenProvided = !(typeof (identity === null || identity === void 0 ? void 0 : identity.token) === 'string');
        if (noAuthTokenProvided) {
            throw new atlas_engine_sdk_1.UnauthorizedError('No auth token provided!');
        }
        const urlWithoutProtocol = this.atlasEngineUrl.split('://')[1] || this.atlasEngineUrl;
        const indexOfLastSlash = urlWithoutProtocol.indexOf('/');
        const urlPath = indexOfLastSlash === -1 ? undefined : urlWithoutProtocol.substring(indexOfLastSlash);
        const socketIoPath = urlPath ? `${urlPath}/socket.io` : '/socket.io';
        const hostPath = indexOfLastSlash === -1 ? this.atlasEngineUrl : this.atlasEngineUrl.split('://')[0] + '://' + urlWithoutProtocol.substring(0, indexOfLastSlash);
        const socketUrl = `${hostPath}/${index_1.socketSettings.namespace}`;
        const socketIoOptions = {
            path: socketIoPath,
            transportOptions: {
                polling: {
                    extraHeaders: {
                        Authorization: identity.token,
                    },
                },
            },
        };
        this.socketCollection[identity.userId] = io(socketUrl, socketIoOptions);
        return this.socketCollection[identity.userId];
    }
}
exports.SocketIoManager = SocketIoManager;
//# sourceMappingURL=SocketIoManager.js.map